import { sidebarCollapsedAtom } from '@/atoms/sidebar.atom';
import { userAtom } from '@/atoms/user.atom';
import { execute } from '@/graphql/execute';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useOrganizations } from '@/hooks/useOrganizations';
import { analytics } from '@/lib/analytics';
import { saveOnboardingSurveyMutation } from '@/mutations/user.mutation';
import { myProjectsQuery } from '@/queries/project.query';
import { projectFoldersQuery } from '@/queries/projectFolder.query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { NextStepProvider, NextStepReact, useNextStep } from 'nextstepjs';
import { useReactRouterAdapter } from 'nextstepjs/adapters/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import OnboardingTourCard from './OnboardingTourCard';
import { selectOnboardingProject } from './selectOnboardingProject';
import { buildTourSteps } from './tourSteps';

// Captures setCurrentStep from useNextStep and exposes it via a ref
const TourBridge = ({
  setCurrentStepRef,
}: {
  setCurrentStepRef: React.MutableRefObject<((step: number, delay?: number) => void) | null>;
}) => {
  const { startNextStep, setCurrentStep } = useNextStep();
  const [started, setStarted] = useState(false);

  setCurrentStepRef.current = setCurrentStep;

  useEffect(() => {
    if (!started) {
      const timer = setTimeout(() => {
        startNextStep('onboarding');
        setStarted(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [started, startNextStep]);

  return null;
};

export const OnboardingTourProvider = ({ children }: { children: React.ReactNode }) => {
  const user = useAtomValue(userAtom);
  const isOnline = useIsOnline();
  const { organization } = useOrganizations();
  const queryClient = useQueryClient();
  const setSidebarCollapsed = useSetAtom(sidebarCollapsedAtom);
  const [, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const completedRef = useRef(false);
  const setCurrentStepRef = useRef<((step: number, delay?: number) => void) | null>(null);

  const { data: projectsData } = useQuery({
    queryKey: ['projects', organization?.id],
    queryFn: () => execute(myProjectsQuery),
    enabled: !!organization?.id,
  });
  const { data: foldersData } = useQuery({
    queryKey: ['projectFolders', organization?.id],
    queryFn: () => execute(projectFoldersQuery),
    enabled: !!organization?.id,
  });

  const firstProjectId = useMemo(
    () => selectOnboardingProject(projectsData?.projects ?? [], foldersData?.projectFolders ?? [])?.id ?? null,
    [foldersData?.projectFolders, projectsData?.projects],
  );
  const steps = useMemo(() => buildTourSteps(firstProjectId), [firstProjectId]);
  const projectsLoaded = !!organization?.id && projectsData !== undefined && foldersData !== undefined;

  // Never start onboarding while offline: the user record comes from the cached
  // copy (which may lack `onboardingTourCompleted`), and onboarding needs the
  // network anyway. This stops the tour from launching on an offline app start.
  //
  // Nor on the auth/onboarding pages: the org exists as soon as it's created,
  // so without this the tour fired over the "invite your colleagues" step
  // instead of waiting for the dashboard.
  const onAuthPage = pathname.startsWith('/auth/');
  const shouldShowTour = !!(
    isOnline &&
    user &&
    !user.onboardingTourCompleted &&
    organization &&
    projectsLoaded &&
    !onAuthPage
  );

  const handleComplete = useCallback(async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    try {
      await execute(saveOnboardingSurveyMutation, {
        data: { tourCompletedAt: new Date().toISOString() },
      });
      queryClient.invalidateQueries({ queryKey: ['me'] });
      analytics.capture('Onboarding Tour Completed');
    } catch (err) {
      console.error('Failed to save tour completion:', err);
    }
  }, [queryClient]);

  const handleSkip = useCallback(
    async (step: number) => {
      if (completedRef.current) return;
      completedRef.current = true;
      try {
        await execute(saveOnboardingSurveyMutation, {
          data: { tourSkippedAt: new Date().toISOString(), tourSkippedAtStep: step },
        });
        queryClient.invalidateQueries({ queryKey: ['me'] });
        analytics.capture('Onboarding Tour Skipped', { step });
      } catch (err) {
        console.error('Failed to save tour skip:', err);
      }
    },
    [queryClient],
  );

  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const handleStepChange = useCallback(
    (stepIndex: number) => {
      const tourSteps = stepsRef.current[0]?.steps;
      if (!tourSteps) return;

      const stepKey = tourSteps[stepIndex]?.content as string;

      // Make sure the sidebar is open when highlighting the project list
      if (stepKey === 'projectList') {
        setSidebarCollapsed(false);
        localStorage.setItem('projects', 'true');
        window.dispatchEvent(new CustomEvent('sidebar-group-open', { detail: { key: 'projects' } }));
        window.setTimeout(() => {
          document.getElementById('onboarding-first-project')?.scrollIntoView({ block: 'center' });
          setCurrentStepRef.current?.(stepIndex);
        }, 150);
      }

      // Open the layers panel when reaching the layer sidebar step
      if (stepKey === 'layerSidebar') {
        const params = new URLSearchParams(window.location.search);
        if (params.get('layers') !== 'true') {
          params.set('layers', 'true');
          setParams(params);
        }
      }

      if (stepKey === 'projectSharing') {
        window.setTimeout(() => setCurrentStepRef.current?.(stepIndex), 150);
      }
    },
    [setParams, setSidebarCollapsed],
  );

  if (!shouldShowTour) return <>{children}</>;

  return (
    <NextStepProvider>
      <NextStepReact
        steps={steps}
        navigationAdapter={useReactRouterAdapter}
        cardComponent={OnboardingTourCard}
        onComplete={handleComplete}
        onSkip={handleSkip}
        onStepChange={handleStepChange}
        shadowRgb='0, 0, 0'
        shadowOpacity='0.6'
        scrollToTop={false}
        noInViewScroll={true}>
        {children}
        <TourBridge setCurrentStepRef={setCurrentStepRef} />
      </NextStepReact>
    </NextStepProvider>
  );
};
